import { z } from 'zod';
/** Picture sizes FreeVideo generates at (multiples of 32), and the size of the finished ad. */
export const ASPECTS = {
    '9:16': {
        width: 544,
        height: 960,
        outWidth: 720,
        outHeight: 1280,
        frame: 'vertical frame',
        label: 'Vertical 9:16 (Reels, Shorts)',
    },
    '16:9': {
        width: 960,
        height: 544,
        outWidth: 1280,
        outHeight: 720,
        frame: 'horizontal frame',
        label: 'Wide 16:9 (YouTube)',
    },
    '1:1': {
        width: 768,
        height: 768,
        outWidth: 1080,
        outHeight: 1080,
        frame: 'square frame',
        label: 'Square 1:1 (Feed)',
    },
};
export const ASPECT_KEYS = Object.keys(ASPECTS);
/**
 * Generation size (multiples of 32) and export size. Standard is what the Fat Cutter ad used; HD takes roughly
 * twice the GPU time per shot (about 2.2x until measured).
 */
export const SIZES = {
    standard: {
        label: 'Standard',
        hint: 'Faster (RTX PRO 6000: about 50 s per 6 s shot). 720p export.',
        dims: {
            '9:16': { width: 544, height: 960, outWidth: 720, outHeight: 1280 },
            '16:9': { width: 960, height: 544, outWidth: 1280, outHeight: 720 },
            '1:1': { width: 768, height: 768, outWidth: 1080, outHeight: 1080 },
        },
    },
    hd: {
        label: 'HD',
        hint: 'Sharper, about 2x the GPU time per shot. 1080p export.',
        dims: {
            '9:16': { width: 768, height: 1344, outWidth: 1080, outHeight: 1920 },
            '16:9': { width: 1344, height: 768, outWidth: 1920, outHeight: 1080 },
            '1:1': { width: 1024, height: 1024, outWidth: 1080, outHeight: 1080 },
        },
    },
};
export const SIZE_KEYS = Object.keys(SIZES);
/** Rough GPU time of an HD shot compared with a standard one. */
export const HD_TIME_FACTOR = 2.2;
/** Generation and export size of a project. */
export function frameSize(aspect, size) {
    const a = (aspect in ASPECTS ? aspect : '9:16');
    const s = (size in SIZES ? size : 'standard');
    return { ...SIZES[s].dims[a], frame: ASPECTS[a].frame };
}
export const ROLES = ['admin', 'editor'];
export const CHARACTER_KINDS = ['organ', 'person', 'mascot', 'product'];
export const QUALITIES = ['light', 'medium', 'high', 'max'];
export const STYLE_PRESETS = [
    '3D animated feature-film style CGI, Pixar-like',
    'Live-action, cinematic',
    '2D hand-drawn cartoon animation',
    'Stop-motion clay animation',
];
export const LANGUAGES = [
    'Hindi',
    'English',
    'Bengali',
    'Marathi',
    'Tamil',
    'Telugu',
    'Gujarati',
    'Punjabi',
    'Kannada',
    'Malayalam',
    'Odia',
];
export const SHOT_MIN_SECONDS = 4;
export const SHOT_MAX_SECONDS = 15;
export const MAX_REFERENCE_PICTURES = 3;
const id = z.string().min(1).max(64);
const text = (max = 4000) => z.string().max(max);
/** Someone or something in a shot. In a shot with reference pictures each becomes <Subject n>, in this order. */
export const castMemberSchema = z.object({
    type: z.enum(['character', 'product']),
    id,
    /** How to describe it in this shot only (e.g. "now swollen and covered in yellow fat"); blank: its usual description. */
    note: text(1000).default(''),
    /** Which reference picture (1–3) shows it, if any. */
    picture: z.number().int().min(1).max(MAX_REFERENCE_PICTURES).nullable().default(null),
});
/** A reference picture given to FreeVideo (<Picture n>). */
export const pictureRefSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('character'), characterId: id, imageIndex: z.number().int().min(0).max(2).default(0) }),
    z.object({ type: z.literal('product'), productId: id }),
    /** A still from another shot's chosen take: its last frame, its middle, or a time in seconds. */
    z.object({
        type: z.literal('shotFrame'),
        shotId: id,
        at: z.union([z.literal('last'), z.literal('mid'), z.number().min(0).max(60)]),
    }),
    z.object({ type: z.literal('upload'), mediaId: id }),
]);
export const dialogueLineSchema = z.object({
    id: z.string().min(1).max(40),
    /** The speaking character; null for a voice that is not a character (then speakerName is used). */
    characterId: id.nullable().default(null),
    speakerName: text(200).default(''),
    /** e.g. "says", "says into the phone", "shouts". */
    delivery: text(200).default('says'),
    /** Overrides the character's voice description for this line (e.g. "the same voice, now panicked"). */
    voice: text(300).default(''),
    /** Blank: the project's language. */
    language: text(40).default(''),
    /** Exact words, in the language's own script (Devanagari for Hindi). */
    text: text(2000).default(''),
    /** 0: the opening picture; n: after the n-th camera cut. */
    cut: z.number().int().min(0).max(5).default(0),
});
export const cutSchema = z.object({
    /** Seconds into the shot. */
    at: z.number().min(0).max(SHOT_MAX_SECONDS),
    description: text(3000).default(''),
});
/** A storyboard still: made on the GPU (FreeVideo's image job) or uploaded. */
export const shotFrameSchema = z.object({
    mediaId: id,
    approved: z.boolean().default(false),
    source: z.enum(['ai', 'upload']).default('ai'),
});
export const shotDataSchema = z.object({
    label: text(120).default(''),
    seconds: z.number().min(SHOT_MIN_SECONDS).max(SHOT_MAX_SECONDS).default(6),
    /** Blank: the project's style. */
    style: text(300).default(''),
    /** Lighting / materials / mood, e.g. "glossy materials, warm cinematic lighting". */
    look: text(500).default(''),
    /** What we see. [Name] names a character of the shot; {dialogue} marks where the spoken lines go. */
    picture: text(6000).default(''),
    camera: text(1000).default(''),
    cast: z.array(castMemberSchema).max(8).default([]),
    pictures: z.array(pictureRefSchema).max(MAX_REFERENCE_PICTURES).default([]),
    /** null: no; "previous": the previous shot's last frame; otherwise a shot id (its last frame). */
    startFrom: z.string().max(64).nullable().default(null),
    /** Shots with reference pictures: one sentence of what happens (blank: made from the picture text). */
    summary: text(2000).default(''),
    /** Shots with reference pictures: what to keep from the pictures (blank: made automatically). */
    retention: text(2000).default(''),
    cuts: z.array(cutSchema).max(4).default([]),
    lines: z.array(dialogueLineSchema).max(12).default([]),
    sound: text(1500).default(''),
    music: text(1000).default(''),
    noOnScreenText: z.boolean().default(true),
    /** Notes for the team (e.g. the director's transition and continuity notes); never sent to FreeVideo. */
    notes: text(4000).default(''),
    /** A fixed seed for the next take (null: a new one each time). */
    seed: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).nullable().default(null),
    /** The storyboard still of this shot. Once approved, the video starts from it (unless it starts from another shot). */
    frame: shotFrameSchema.nullable().default(null),
});
export const cardDataSchema = z.object({
    /** One line per row; rows appear one after another. */
    text: text(300).default('TWO WEEKS LATER'),
    seconds: z.number().min(1).max(10).default(2.5),
    /** #rrggbb, or "bamboo" for the green stalks picture. */
    background: z.string().max(20).default('bamboo'),
    textColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .default('#E01B1B'),
    outlineColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .default('#5A0000'),
    /** Rows appear one by one. */
    animate: z.boolean().default(true),
});
export const ctaSchema = z.object({
    enabled: z.boolean().default(true),
    buttonText: text(40).default('ORDER NOW'),
    linkLabel: text(40).default('link'),
    /** Seconds into the last shot. */
    startSec: z.number().min(0).max(30).default(2),
    showArrow: z.boolean().default(true),
    showLogo: z.boolean().default(true),
});
// ── Requests ──────────────────────────────────────────────────────────────
export const loginSchema = z.object({ email: z.email().max(200), password: z.string().min(1).max(200) });
export const createUserSchema = z.object({
    email: z.email().max(200),
    name: z.string().trim().min(1).max(120),
    password: z.string().min(10, 'At least 10 characters').max(200),
    role: z.enum(ROLES).default('editor'),
});
export const updateUserSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    role: z.enum(ROLES).optional(),
    active: z.boolean().optional(),
    password: z.string().min(10, 'At least 10 characters').max(200).optional(),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: z.string().min(10, 'At least 10 characters').max(200),
});
const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const brandColorsSchema = z.object({
    primary: hex.default('#1E7B34'),
    accent: hex.default('#E1141E'),
    cardBackground: hex.default('#A0C428'),
    cardText: hex.default('#E01B1B'),
    cardOutline: hex.default('#5A0000'),
});
export const updateBrandSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    logoId: id.nullable().optional(),
    buttonText: z.string().trim().min(1).max(40).optional(),
    linkLabel: z.string().trim().max(40).optional(),
    colors: brandColorsSchema.optional(),
});
export const knowledgeDocSchema = z.object({
    title: z.string().trim().min(1).max(200),
    kind: z.enum(['qa', 'claims', 'other']).default('other'),
    body: z.string().max(200_000),
    useInAi: z.boolean().default(true),
});
export const updateProductSchema = z.object({
    title: z.string().trim().min(1).max(300).optional(),
    labelImageId: id.nullable().optional(),
    promptDescription: z.string().max(2000).optional(),
    notes: z.string().max(10_000).optional(),
    allowedClaims: z.string().max(10_000).optional(),
});
export const characterSchema = z.object({
    name: z.string().trim().min(1).max(120),
    kind: z.enum(CHARACTER_KINDS).default('person'),
    callName: z.string().max(200).default(''),
    visualDescription: z.string().max(3000).default(''),
    voiceDescription: z.string().max(500).default(''),
    language: z.string().max(40).default('Hindi'),
    imageIds: z.array(id).max(3).default([]),
    notes: z.string().max(5000).default(''),
    /** A picture of the clothes the character wears (the character sheet is drawn in them). */
    outfitImageId: id.nullable().default(null),
    /** The background locked to the character: every still and shot with them is set there. */
    backgroundId: id.nullable().default(null),
});
/** A place with reference pictures, which can be locked to characters. */
export const backgroundSchema = z.object({
    name: z.string().trim().min(1).max(120),
    /** English, used in prompts: "a busy highway dhaba at night with string lights and plastic tables". */
    description: z.string().max(2000).default(''),
    imageIds: z.array(id).max(3).default([]),
    notes: z.string().max(5000).default(''),
});
export const createProjectSchema = z.object({
    title: z.string().trim().min(1).max(200),
    productId: id.nullable().default(null),
    language: z.string().max(40).default('Hindi'),
    style: z.string().trim().min(1).max(300).default(STYLE_PRESETS[0]),
    aspect: z.enum(ASPECT_KEYS).default('9:16'),
    size: z.enum(SIZE_KEYS).default('hd'),
    quality: z.enum(QUALITIES).default('light'),
    script: z.string().max(20_000).default(''),
    castIds: z.array(id).max(20).default([]),
});
export const updateProjectSchema = z.object({
    title: z.string().trim().min(1).max(200).optional(),
    productId: id.nullable().optional(),
    language: z.string().max(40).optional(),
    style: z.string().trim().min(1).max(300).optional(),
    aspect: z.enum(ASPECT_KEYS).optional(),
    quality: z.enum(QUALITIES).optional(),
    size: z.enum(SIZE_KEYS).optional(),
    script: z.string().max(20_000).optional(),
    castIds: z.array(id).max(20).optional(),
    cta: ctaSchema.optional(),
});
export const createShotSchema = z.object({
    kind: z.enum(['shot', 'card']).default('shot'),
    /** Insert after this shot (default: at the end). */
    afterId: id.nullable().default(null),
    data: z.unknown().optional(),
});
export const updateShotSchema = z.object({
    data: z.unknown().optional(),
    rawPrompt: z.string().max(20_000).nullable().optional(),
});
export const reorderShotsSchema = z.object({ ids: z.array(id).min(1).max(200) });
export const generateSchema = z.object({
    /** Shots to make; empty or missing: every shot of the project. */
    shotIds: z.array(id).max(200).optional(),
    /** Only shots that have no finished take yet (for "generate all"). */
    missingOnly: z.boolean().default(true),
});
export const remakeSchema = z.object({ newSeed: z.boolean().default(true) });
export const draftStoryboardSchema = z.object({
    replace: z.boolean().default(true),
    /** Optional extra instructions for the AI. */
    instructions: z.string().max(4000).default(''),
});
/** Storyboard stills. */
export const makeFrameSchema = z.object({
    /** Change the current still as described, instead of drawing a new one. */
    edit: z.string().trim().max(1000).optional(),
});
export const makeFramesSchema = z.object({
    /** Only shots that have no still yet. */
    missingOnly: z.boolean().default(true),
});
export const approveFrameSchema = z.object({ approved: z.boolean().default(true) });
export const setFrameSchema = z.object({ mediaId: id.nullable() });
